/**
 * The frame of every settings page: a side menu with the groups on the left (a "Menu" button
 * that opens the same list on a phone) and the page on the right. The pages keep their own layout.
 */

import { useEffect, useState, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ADMIN_NAV, activeAdminGroup, isAdminNavActive, type AdminNavItem } from "../navigation/admin-nav";

const environment = import.meta.env.VITE_ENVIRONMENT;
const isDev = environment === "development" || environment === "sandbox";

const itemClass = (active: boolean) =>
  `flex items-center justify-between min-h-11 rounded-md px-3 py-2 text-sm ${
    active
      ? isDev
        ? "bg-red-200 font-semibold text-red-900"
        : "bg-sea-100 font-semibold text-sea-900"
      : isDev
        ? "text-gray-700 hover:bg-red-50"
        : "text-gray-700 hover:bg-sea-50"
  }`;

export function adminLabel(t: (k: string) => string, item: AdminNavItem): string {
  return t(item.labelKey ?? `nav.admin.items.${item.key}`);
}

function SideMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const current = activeAdminGroup(pathname, search);
  // only the group of the page is open; the others are one line each, so the menu fits on a screen
  const [opened, setOpened] = useState<string[]>([]);
  const [closed, setClosed] = useState<string[]>([]);
  const isOpen = (key: string) => (opened.includes(key) || key === current) && !closed.includes(key);
  const toggle = (key: string) => {
    if (isOpen(key)) {
      setOpened((p) => p.filter((k) => k !== key));
      setClosed((p) => [...p, key]);
    } else {
      setClosed((p) => p.filter((k) => k !== key));
      setOpened((p) => [...p, key]);
    }
  };
  // moving to another group shows that group, and the one that was open folds
  useEffect(() => setClosed([]), [current]);
  return (
    <nav aria-label={t("nav.admin.menu")} className="space-y-1">
      <Link to="/settings" onClick={onNavigate} className={itemClass(pathname === "/settings")}>
        {t("nav.admin.overview")}
      </Link>
      {ADMIN_NAV.map((group) => {
        const open = isOpen(group.key);
        return (
          <div key={group.key}>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={`menu-group-${group.key}`}
              onClick={() => toggle(group.key)}
              className={`flex min-h-11 w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-semibold ${current === group.key ? "text-gray-900" : "text-gray-600"} hover:bg-gray-50`}
            >
              <span>{t(`nav.admin.groups.${group.key}`)}</span>
              <span aria-hidden="true" className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}>
                ›
              </span>
            </button>
            <ul id={`menu-group-${group.key}`} hidden={!open} className="mb-1 space-y-0.5 pl-2">
              {group.items.map((item) => (
                <li key={item.key}>
                  <Link to={item.path} onClick={onNavigate} aria-current={isAdminNavActive(item, pathname, search) ? "page" : undefined} className={itemClass(isAdminNavActive(item, pathname, search))}>
                    <span>{adminLabel(t, item)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export default function AdminShell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const { pathname, search } = useLocation();
  // a page opens at its top: the frame keeps the scroll position of the page before it
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    setOpen(false);
  }, [pathname, search]);
  return (
    <div className="mx-auto max-w-7xl lg:flex lg:gap-8">
      <aside onKeyDown={event => { if (event.key === "Escape" && open) { setOpen(false); menuButton.current?.focus(); } }} className="lg:admin-sticky lg:sticky lg:w-60 lg:shrink-0 lg:self-start lg:overflow-y-auto">
        <button type="button" ref={menuButton} className="btn btn-secondary mb-3 w-full lg:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="settings-menu">
          {t("nav.admin.menu")}
        </button>
        <div id="settings-menu" className={`${open ? "block" : "hidden"} lg:block`}>
          <SideMenu onNavigate={() => setOpen(false)} />
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
